import type { ArrayElementType } from '@/core/types/gengeric';
import type { MultilanguageValue } from '@/core/utils/languages';
import {
  ChannelManagerCode,
  DetermineAgeMethod,
  type GetTravelLinePlacingVariantsQuery,
  OperationStatus,
  TravelLineMasterStep,
} from '@/graphql/generated';

export type TLPlacingVariantPlacing = ArrayElementType<
  ArrayElementType<
    NonNullable<GetTravelLinePlacingVariantsQuery['travelLinePlacingVariants']>['placingVariantsTarification']
  >['suggestedPlacingVariants']
>['placingVariant'];

export type PlacingVariant = {
  TLName?: string;
  extranetName: string;
  maxNoSeatQuantity?: number | null;
  mandatory: boolean;
  isConflicted?: boolean;
  roomPlacingCID?: string;
  placingVariant: TLPlacingVariantPlacing;
  defaultAvailable?: boolean;
  isNeedConfirm?: boolean;
  pricePerRoom?: boolean;
};

export type PlacingVariantsRoom = {
  roomCid: string;
  roomName: MultilanguageValue[];
  items: PlacingVariant[];
  selected: PlacingVariant[];
};

export type TLPlacingVariant = ArrayElementType<
  ArrayElementType<
    NonNullable<GetTravelLinePlacingVariantsQuery['travelLinePlacingVariants']>['placingVariantsTarification']
  >['suggestedPlacingVariants']
> & { isConflicted?: boolean };

export type TLIntegrationHotelData = {
  id: string;
  name: string;
  uncompletedInteractionId?: string | null;
};

export type TLIntegrationData = {
  channelManagerCode: ChannelManagerCode;
  externalIntegration: {
    externalId?: string;
    integrationUrl?: string;
    externalHotel?: { hotelId: string; hotelName: string };
  };
  master?: {
    __typename?: 'TravelLineIntegrationMasterView';
    masterStep: TravelLineMasterStep;
    hotelSetCid: string;
    steps: {
      __typename?: 'TravelLineMasterStepsView';
      integrationSettings?: {
        __typename?: 'IntegrationSettings';
        penaltySchemeCode: string;
        supplierPayRuleCode: string;
        connectionName: Array<{ __typename?: 'LangString'; langCode: string; value: string }>;
        checkInOutSettings: {
          __typename?: 'CheckInOutSettings';
          checkInTime: string;
          checkOutTime: string;
          nextDayCheckout: boolean;
        };
      } | null;
      roomCategoriesMapping?: {
        __typename?: 'RoomCategoriesMapping';
        roomCategories: Array<{
          __typename?: 'RoomCategoryView';
          cid: string;
          shortName: string;
          isActive: boolean;
          type: { __typename?: 'RoomType'; cid: string; name: string };
          name: Array<{ __typename?: 'LangString'; langCode: string; value: string }>;
          description: Array<{ __typename?: 'LangString'; langCode: string; value: string }>;
          seat: { __typename?: 'RoomSeat'; baseQuantity: number; extQuantity: number };
          operations: {
            __typename?: 'RoomCategoryOperations';
            delete: OperationStatus;
            edit: OperationStatus;
            fullEdit: boolean;
          };
        }>;
        translationTable: Array<{
          __typename?: 'ExternalObjectTranslationView';
          cid?: string | null;
          externalObject: { __typename?: 'ExternalObjectView'; id: string; name: string };
        }>;
      } | null;
      touristTypesMapping?: {
        __typename?: 'TouristTypesMapping';
        touristTypesRefreshRequired: boolean;
        ageSettings: {
          __typename?: 'AgeSettings';
          initialAge: number;
          noSeatAge?: number | null;
          determineAgeMethod: DetermineAgeMethod;
          categories: {
            __typename?: 'AgeCategories';
            values: Array<{ __typename?: 'AgeCategory'; from: number; to: number }>;
          };
        };
      } | null;
      placingVariantsMapping?: {
        __typename?: 'PlacingVariantsMapping';
        requiredPlacingVariants: Array<{
          __typename?: 'RequiredPlacingVariant';
          roomCategoryCid: string;
          placingVariantName: string;
        }>;
      } | null;
      serviceGroupsMapping?: {
        __typename?: 'ServiceGroupsMapping';
        serviceGroups: Array<{
          __typename?: 'TravelLineServiceGroupView';
          canChange: boolean;
          cid: string;
          name: Array<{ __typename?: 'LangString'; langCode: string; value: string }>;
        }>;
        translationTable: Array<{
          __typename?: 'ExternalObjectTranslationView';
          cid?: string | null;
          externalObject: { __typename?: 'ExternalObjectView'; id: string; name: string };
        }>;
      } | null;
    };
  } | null;
};

export enum ChannelManagerCustomError {
  ChannelManagerAdditionalMappingRequiredError = 'ChannelManagerAdditionalMappingRequiredError',
}
